/* Content → Paid content → Vaults → Content bundles. Named groups of content sold
   together at a set price. Each row saves on its own as soon as it's complete
   (content + a max price): created on first save, updated on change, deleted on
   ×. The sales-ladder toggle saves immediately. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { ContentBundle, ContentBundleBody, ShapeContentOverride } from '../../../../api/client';
import { useAutosave, type AutosaveStatus } from '../../../../hooks/useAutosave';
import { useSalesCatalogue, type SalesCatalogue } from '../../SalesLadder/useSalesCatalogue';
import { isVideoItem } from '../../SalesLadder/MediaBadge';
import { Badge, Btn, Card, ErrorLine, Loading, NumInput, SaveStatus, THead, Toggle, Warn, XButton } from '../kit';
import { font, mergeStatus } from '../style';
import { C } from '../tokens';
import { MediaPicker } from '../MediaPicker';
import { ThumbStrip } from '../ThumbStrip';
import { useResolveIds } from '../resolveIds';
import { matchShape, rangeText } from './shapes';

interface Draft { content_ids: string[]; price: number | null; min_price: number | null; is_active: boolean }
interface RowState extends Draft { uid: string; id: string | null }

const COLS = 'minmax(0,1fr) 80px 80px 60px 26px';
let seq = 0;

const complete = (d: Draft) => d.content_ids.length > 0 && d.price !== null && d.price > 0;

export function BundlesSection({ accountId, shapes }: { accountId: string; shapes: ShapeContentOverride[] }) {
  const { t } = useTranslation('assistantCompact');
  const cat = useSalesCatalogue(accountId);
  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [ladder, setLadder] = useState<boolean | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, { status: AutosaveStatus; error: string | null; retry: () => void }>>({});

  useEffect(() => {
    let off = false;
    setLoading(true);
    api.getContentBundles(accountId).then(
      (r) => {
        if (off) return;
        const sorted = [...r.bundles].sort((a, b) => a.display_order - b.display_order || a.created_at.localeCompare(b.created_at));
        setRows(sorted.map((b: ContentBundle) => ({ uid: `b-${b.id}`, id: String(b.id), content_ids: [...b.content_ids], price: b.price, min_price: b.min_price, is_active: b.is_active })));
      },
      (e) => { if (!off) setErr(e instanceof Error ? e.message : t('content.loadFailed')); },
    ).finally(() => { if (!off) setLoading(false); });
    api.getUseBundles(accountId).then((r) => { if (!off) setLadder(r.use_bundles); }, () => { if (!off) setLadder(false); });
    return () => { off = true; };
  }, [accountId, t]);

  const resolving = useResolveIds(accountId, cat, rows.flatMap((r) => r.content_ids));

  const setLadderSaved = async (v: boolean) => {
    const prev = ladder;
    setLadder(v);
    try { await api.setUseBundles(accountId, v); } catch (e) { setLadder(prev); setErr(e instanceof Error ? e.message : t('save.failed')); }
  };
  const patch = (uid: string, p: Partial<RowState>) => setRows((rs) => rs.map((r) => (r.uid === uid ? { ...r, ...p } : r)));
  const remove = async (r: RowState) => {
    setRows((rs) => rs.filter((x) => x.uid !== r.uid));
    setStatuses((s) => { const n = { ...s }; delete n[r.uid]; return n; });
    if (!r.id) return;
    try { await api.deleteContentBundle(accountId, r.id); } catch (e) { setRows((rs) => [...rs, r]); setErr(e instanceof Error ? e.message : t('save.failed')); }
  };
  const onStatus = useCallback((uid: string, s: { status: AutosaveStatus; error: string | null; retry: () => void }) => {
    setStatuses((prev) => (prev[uid]?.status === s.status && prev[uid]?.error === s.error ? prev : { ...prev, [uid]: s }));
  }, []);

  const incomplete = rows.filter((r) => !complete(r)).length;
  const merged = mergeStatus(...Object.values(statuses).filter((s) => s.status !== 'invalid'));
  const pickRow = rows.find((r) => r.uid === picking) ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ font: font(600, 13), color: C.text }}>{t('bundles.title')}</span>
        <Badge>{t('bundles.optional')}</Badge>
        <span style={{ font: font(400, 12), color: C.muted }}>{rows.length}</span>
        <span style={{ flex: 1 }} />
        <SaveStatus status={merged.status} error={merged.error} onRetry={merged.retry} idleLabel />
      </div>
      <div style={{ font: font(400, 11.5), color: C.sub }}>{t('bundles.desc')}</div>
      <Card style={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 28, padding: '10px 14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ font: font(500, 13), color: C.text }}>{t('bundles.ladder')}</span>
            <span style={{ font: font(400, 11.5), color: C.sub }}>{t('bundles.ladderHelp')}</span>
          </div>
          <Toggle on={!!ladder} disabled={ladder === null} onChange={(v) => void setLadderSaved(v)} />
        </div>
      </Card>
      {err && <ErrorLine>{err}</ErrorLine>}
      {loading ? <Loading /> : (
        <Card>
          <THead cols={COLS}><span>{t('bundles.content')}</span><span>{t('bundles.max')}</span><span>{t('bundles.min')}</span><span>{t('bundles.active')}</span><span /></THead>
          {rows.map((r) => (
            <BundleRow key={r.uid} accountId={accountId} row={r} cat={cat} resolving={resolving} onPatch={(p) => patch(r.uid, p)} onRemove={() => void remove(r)} onChoose={() => setPicking(r.uid)} onStatus={onStatus} />
          ))}
          {!rows.length && <div style={{ padding: '12px 14px', font: font(400, 12), color: C.muted }}>{t('bundles.none')}</div>}
        </Card>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 32 }}>
        {incomplete > 0 && <Warn>{t('bundles.needs', { count: incomplete })}</Warn>}
        <span style={{ flex: 1 }} />
        <Btn kind="tint" onClick={() => setRows((rs) => [...rs, { uid: `n-${++seq}`, id: null, content_ids: [], price: null, min_price: null, is_active: true }])}>{t('bundles.add')}</Btn>
      </div>
      {pickRow && (
        <MediaPicker
          accountId={accountId}
          title={t('bundles.pickTitle', { n: rows.indexOf(pickRow) + 1 })}
          initialSelected={pickRow.content_ids}
          fit={(p, v) => {
            if (!p && !v) return { text: t('bundles.pickHint'), tone: 'ok' };
            const s = matchShape(shapes, p, v);
            if (!s) return { text: t('bundles.noShape'), tone: 'warn' };
            const label = [s.shape.photos ? t('pricing.photosN', { range: rangeText(s.shape.photos) }) : '', s.shape.videos ? t('pricing.videosN', { range: rangeText(s.shape.videos) }) : ''].filter(Boolean).join(' + ');
            return { text: t('bundles.matches', { shape: label, price: s.price?.[0] ?? '—', floor: s.price?.[1] ?? '—' }), tone: 'ok' };
          }}
          onConfirm={(ids, items) => {
            const vids = items.filter((i) => isVideoItem(i.type)).length;
            const s = matchShape(shapes, items.length - vids, vids);
            const p: Partial<RowState> = { content_ids: ids };
            if (pickRow.price === null && s?.price?.[0]) { p.price = s.price[0]; p.min_price = s.price[1] ?? null; }
            patch(pickRow.uid, p);
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}

function BundleRow({ accountId, row, cat, resolving, onPatch, onRemove, onChoose, onStatus }: {
  accountId: string;
  row: RowState;
  cat: SalesCatalogue;
  resolving: boolean;
  onPatch: (p: Partial<RowState>) => void;
  onRemove: () => void;
  onChoose: () => void;
  onStatus: (uid: string, s: { status: AutosaveStatus; error: string | null; retry: () => void }) => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const idRef = useRef(row.id);
  idRef.current = row.id;
  // Set on ×, so a save flushed on unmount (or still in flight) can't resurrect it.
  const removed = useRef(false);
  const draft: Draft = { content_ids: row.content_ids, price: row.price, min_price: row.min_price, is_active: row.is_active };
  const auto = useAutosave<Draft>({
    value: draft,
    validate: (d) => (!complete(d) ? t('bundles.incomplete') : d.min_price !== null && (d.min_price <= 0 || d.min_price > (d.price ?? 0)) ? t('bundles.minAboveMax') : null),
    save: async (d) => {
      if (removed.current) return;
      const body: ContentBundleBody = { content_ids: d.content_ids, price: d.price ?? 0, min_price: d.min_price, is_active: d.is_active };
      if (idRef.current) {
        await api.updateContentBundle(accountId, idRef.current, body);
      } else {
        const r = await api.createContentBundle(accountId, body);
        const id = String(r.bundle.id);
        if (removed.current) { await api.deleteContentBundle(accountId, id).catch(() => {}); return; }
        idRef.current = id;
        onPatch({ id });
      }
    },
  });
  useEffect(() => { onStatus(row.uid, auto); }, [auto.status, auto.error]); // eslint-disable-line react-hooks/exhaustive-deps

  const mix = (() => {
    if (!row.content_ids.length) return t('bundles.noItems');
    const known = row.content_ids.map((id) => cat.byId.get(id)).filter(Boolean);
    const v = known.filter((i) => isVideoItem(i!.type)).length;
    const p = known.length - v;
    return known.length ? [p ? t('picker.photos', { count: p }) : '', v ? t('picker.videos', { count: v }) : ''].filter(Boolean).join(' · ') : t('picker.items', { count: row.content_ids.length });
  })();
  const bad = auto.status === 'invalid' && complete(draft);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'center', minHeight: 48, padding: '6px 14px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <Btn kind="ghost" height={26} icon="layers" onClick={onChoose} style={{ color: C.purplePale, borderColor: row.content_ids.length ? C.borderHi : 'color-mix(in srgb, var(--om-accent) 60%, transparent)' }}>{row.content_ids.length ? t('bundles.edit') : t('bundles.choose')}</Btn>
        {row.content_ids.length ? <ThumbStrip accountId={accountId} ids={row.content_ids} byId={cat.byId} loading={resolving} onClick={onChoose} title={mix} /> : <span style={{ font: font(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{mix}</span>}
      </div>
      <NumInput value={row.price} onChange={(v) => onPatch({ price: v })} min={0} max={99999} allowEmpty placeholder="0.00" width="100%" />
      <NumInput value={row.min_price} onChange={(v) => onPatch({ min_price: v })} min={0} max={99999} allowEmpty placeholder="0.00" width="100%" title={bad ? auto.error ?? undefined : undefined} />
      <Toggle on={row.is_active} onChange={(v) => onPatch({ is_active: v })} />
      <XButton onClick={() => { removed.current = true; onRemove(); }} title={t('remove')} />
    </div>
  );
}
